import { Word, CategoryScores } from '@/types/assessmentTypes';
import { ASSESSMENT_CONSTANTS } from '@/constants/assessmentConstants';

export const calculateCategoryScores = (words: Word[], selectedWordIds: Set<number>) => {
  const categoryScores: CategoryScores = {
    dominance: 0,
    extraversion: 0,
    patience: 0,
    formality: 0,
    objectivity: 0
  };

  const categoryMaxScores: CategoryScores = {
    dominance: 0,
    extraversion: 0,
    patience: 0,
    formality: 0,
    objectivity: 0
  };

  // Calculate actual scores and maximum possible scores
  words.forEach(word => {
    categoryMaxScores[word.category] += word.intensity;
    
    if (selectedWordIds.has(word.id)) {
      categoryScores[word.category] += word.intensity;
    }
  });

  return { categoryScores, categoryMaxScores };
};

export const calculateZScore = (rawScore: number, maxScore: number): number => {
  if (maxScore === 0) return 0;
  const percentage = rawScore / maxScore;
  // Convert percentage to z-score using normal distribution
  // Scale to -2 to +2 range for better distribution
  const z = (percentage - 0.5) * ASSESSMENT_CONSTANTS.SCORING.SCALE_FACTOR;
  return Math.round(z * 10) / 10; // Round to 1 decimal place
};

export const balanceScores = (zScores: CategoryScores): CategoryScores => {
  // Only apply range clamping and decimal rounding - no artificial balancing
  Object.keys(zScores).forEach(key => {
    const k = key as keyof CategoryScores;
    if (k !== 'objectivity') { // Don't apply range limits to objectivity
      zScores[k] = Math.max(
        ASSESSMENT_CONSTANTS.Z_SCORE_RANGE.MIN, 
        Math.min(ASSESSMENT_CONSTANTS.Z_SCORE_RANGE.MAX, zScores[k])
      );
    }
    zScores[k] = Math.round(zScores[k] * 10) / 10;
  });

  return zScores;
};

// Calculate objectivity model 3 - Reverse scoring method
export const calculateObjectivityModel3 = (words: Word[], selectedWordIds: Set<number>): number => {
  let score = 100; // Start at 100%
  
  // Get only objectivity words that were selected
  const selectedObjectivityWords = words.filter(word => 
    word.category === 'objectivity' && selectedWordIds.has(word.id)
  );
  
  // Deduct points based on intensity (reverse scoring)
  selectedObjectivityWords.forEach(word => {
    switch (word.intensity) {
      case 1: // Very subjective words like "emotional", "intuitive", "impulsive", "anxious"
        score -= 20;
        break;
      case 2: // Moderately subjective words like "spontaneous"
        score -= 12;
        break;
      case 3: // Neutral words like "detached", "realistic", "pragmatic", "critical"
        score -= 6;
        break;
      case 4: // Objective words like "systematic", "rational", "methodical", "empirical"
        score -= 2;
        break;
      case 5: // Highly objective words like "analytical", "logical"
        score -= 0; // No deduction
        break;
    }
  });
  
  // Ensure score doesn't go below 0
  return Math.max(0, score);
};

// Calculate triple objectivity models (renamed from dual)
export const calculateTripleObjectivityScores = (scores: CategoryScores, words: Word[], selectedWordIds: Set<number>) => {
  // Model 1: Current calculation (Order - Connection) / 2
  const objectivityModel1 = (scores.formality + (-scores.extraversion)) / 2;
  
  // Model 2: Direct measurement from objectivity words
  const objectivityModel2 = scores.objectivity || 0;
  
  // Model 3: Reverse scoring method
  const objectivityModel3Score = calculateObjectivityModel3(words, selectedWordIds);
  // Convert percentage to z-score for consistency
  const objectivityModel3 = (objectivityModel3Score - 50) / 25; // Convert 0-100 to approx -2 to +2 range
  
  return {
    objectivityModel1: Math.round(objectivityModel1 * 10) / 10,
    objectivityModel2: Math.round(objectivityModel2 * 10) / 10,
    objectivityModel3: Math.round(objectivityModel3 * 10) / 10,
    objectivityModel3Raw: objectivityModel3Score // Keep raw percentage for display
  };
};

// Keep the old function name for backward compatibility but mark as deprecated
export const calculateDualObjectivityScores = (scores: CategoryScores) => {
  console.warn('calculateDualObjectivityScores is deprecated, use calculateTripleObjectivityScores instead');
  
  // Model 1: Current calculation (Order - Connection) / 2
  const objectivityModel1 = (scores.formality + (-scores.extraversion)) / 2;
  
  // Model 2: Direct measurement from objectivity words
  const objectivityModel2 = scores.objectivity || 0;
  
  return {
    objectivityModel1: Math.round(objectivityModel1 * 10) / 10,
    objectivityModel2: Math.round(objectivityModel2 * 10) / 10
  };
};
